import { pacificToday } from '../../../shared/dates';
import type { Persona, PlaybookVariant, PosSystem } from '../../../shared/enums';
import {
  firstNameOf,
  personaForBusinessType,
  personaForPlaceTypes,
  personaForSignal,
  renderMessage,
} from '../../../shared/playbook';
import type { MergeFields } from '../../../shared/templates';
import type {
  IsoDate,
  Lead,
  PlaybookMessage,
  Signal,
  StoredRoute,
  Visit,
} from '../../../shared/types';
import { POS_LABELS } from '../ui/labels';
import { followUpTargets } from './followupRoute';
import { filedAgo, whyText } from './signalRank';

/**
 * The daily brief (D89) — what Artur does today, composed from the local mirror alone so it opens
 * on the sidewalk with no signal. Three lists that never overlap:
 *
 *  1. Follow-ups due   — what he promised, overdue first (the Today rules, D9).
 *  2. Calls to make    — new businesses worth a visit that have a phone and are not already a stop.
 *  3. Walk-in route    — today's route, in order, each stop with the reason the planner gave it.
 *
 * Every item carries the reason it is on the list and the message library draft that fits it (D88).
 * Nothing here sends anything: a draft is copy for Artur to send from his own machine.
 */

export interface BriefDraft {
  message: PlaybookMessage;
  subject: string | null;
  body: string;
}

export type BriefKind = 'follow_up' | 'call' | 'stop';

export interface BriefItem {
  kind: BriefKind;
  daysOverdue?: number;
  /** Stable within a brief: the lead, the signal or the route stop this item stands for. */
  key: string;
  lead_id: string | null;
  signal_id: string | null;
  name: string;
  /** Address or city, when the row knows one. */
  where: string | null;
  /** Why this is on today's list, in the words the rest of the app already uses. */
  reason: string;
  phone: string | null;
  persona: Persona;
  /** The draft that fits, and the other messages worth having to hand at the same door. */
  draft: BriefDraft | null;
  alternates: BriefDraft[];
  /** Worked already: a touch was logged at this door today (the Route tab's rule, D27). The row
   *  stays in place, struck through, so the day reads as a list he is working down. */
  done: boolean;
}

export interface Brief {
  day: IsoDate;
  followUps: BriefItem[];
  calls: BriefItem[];
  stops: BriefItem[];
  /** Today's route as the device has it, when there is one. `planned` means Plan tomorrow built
   *  and he accepted it (D83); a route he assembled by hand in the Route tab has it false. */
  route: { area: string | null; planned: boolean; stops: number } | null;
  /** What the brief could not know, said plainly rather than left blank (project guardrail). */
  gaps: string[];
}

/** How many new businesses the call list offers before it is a list nobody works. */
export const CALL_LIMIT = 8;

// ---------------------------------------------------------------- choosing the message

const monthOf = (day: IsoDate): number => Number(day.slice(5, 7));

/** November and December, when the library pauses retail and changes what is said (§4). */
export const isRetailQuarter = (day: IsoDate): boolean => monthOf(day) >= 11;

/** The message for a door he is walking into: the owner-present opener, with the owner-not-there
 *  one and the card note beside it, because he does not know which he will get until he is in. */
export const stopVariants = (persona: Persona, day: IsoDate): PlaybookVariant[] =>
  persona === 'retail' && isRetailQuarter(day)
    ? ['walk_in_owner_q4', 'walk_in_staff', 'card_note']
    : ['walk_in_owner', 'walk_in_staff', 'card_note'];

/** The message for a call: the voicemail, with what to say to whoever answers instead. */
export const callVariants = (persona: Persona, day: IsoDate): PlaybookVariant[] =>
  persona === 'retail' && isRetailQuarter(day)
    ? ['voicemail_q4', 'voicemail', 'gatekeeper']
    : ['voicemail', 'gatekeeper'];

/**
 * The follow-up message is the library's sequence position: the first touch is touch 1, so a lead
 * with one visit behind it gets touch 2. Past touch 5 the sequence is over — the library's rule is
 * to close the loop once and not start again, so touch 5 stays the message.
 */
export const followUpVariants = (touches: number, preOpen: boolean): PlaybookVariant[] => {
  const next = Math.min(Math.max(touches + 1, 2), 5);
  if (next === 3) return preOpen ? ['follow_up_3_pre_open', 'follow_up_3'] : ['follow_up_3'];
  return [`follow_up_${next}` as PlaybookVariant];
};

// ---------------------------------------------------------------- rendering

/** The street without its number: "1420 Newport Blvd" → "Newport Blvd". */
export const streetNameOf = (address: string | null): string | null => {
  const street = (address ?? '').split(',')[0]?.trim() ?? '';
  const named = street.replace(/^[\d-]+\s+/, '').trim();
  return named.length ? named : null;
};

/** The systems the library is willing to name back to an owner. "Other", "cash only" and "unknown"
 *  are not names, and a draft that says "your page runs on Other" is worse than an unfilled one. */
const NAMEABLE_POS: readonly PosSystem[] = [
  'toast',
  'clover',
  'spoton',
  'touchbistro',
  'lightspeed',
  'shopify',
  'chownow',
  'aloha',
  'micros',
];

const competitorName = (lead: Lead): string | null =>
  NAMEABLE_POS.includes(lead.current_pos) ? POS_LABELS[lead.current_pos] : null;

export const leadFields = (lead: Lead): MergeFields => ({
  first_name: firstNameOf(lead.owner_name) ?? undefined,
  owner_name: lead.owner_name ?? undefined,
  business_name: lead.business_name,
  city: lead.neighborhood ?? undefined,
  street_address: lead.address ?? undefined,
  street_name: streetNameOf(lead.address) ?? undefined,
  incumbent: competitorName(lead) ?? undefined,
});

export const signalFields = (signal: Signal): MergeFields => ({
  first_name: firstNameOf(signal.contact_name ?? signal.owner_name) ?? undefined,
  owner_name: signal.contact_name ?? signal.owner_name ?? undefined,
  business_name: signal.business_name,
  city: signal.city ?? undefined,
  street_address: signal.address ?? undefined,
  street_name: streetNameOf(signal.address) ?? undefined,
});

type Library = ReadonlyMap<string, PlaybookMessage>;

export const indexPlaybook = (messages: readonly PlaybookMessage[]): Library =>
  new Map(messages.map((m) => [`${m.persona}:${m.variant}`, m]));

const draftOf = (
  library: Library,
  persona: Persona,
  variant: PlaybookVariant,
  fields: MergeFields,
): BriefDraft | null => {
  const message = library.get(`${persona}:${variant}`);
  if (!message) return null;
  const rendered = renderMessage(message, fields);
  return { message, subject: rendered.subject, body: rendered.body };
};

/** The first variant the persona actually has becomes the draft; the rest ride along as alternates.
 *  A persona is allowed to be missing a variant (the library does not write every one for every
 *  persona), and a missing one is simply absent rather than borrowed from another persona. */
const draftsFor = (
  library: Library,
  persona: Persona,
  variants: readonly PlaybookVariant[],
  fields: MergeFields,
): { draft: BriefDraft | null; alternates: BriefDraft[] } => {
  const found = variants.flatMap((v) => {
    const d = draftOf(library, persona, v, fields);
    return d ? [d] : [];
  });
  return { draft: found[0] ?? null, alternates: found.slice(1) };
};

// ---------------------------------------------------------------- the brief

export interface BriefInput {
  today: IsoDate;
  leads: readonly Lead[];
  visits: readonly Visit[];
  /** The stored copy of the New businesses list (D80). */
  signals: readonly Signal[];
  /** Today's route as the device holds it (D84), or null when none was planned. */
  route: StoredRoute | null;
  playbook: readonly PlaybookMessage[];
}

const visitsByLead = (visits: readonly Visit[]): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const v of visits) counts.set(v.lead_id, (counts.get(v.lead_id) ?? 0) + 1);
  return counts;
};

const pacificDay = (iso: string): IsoDate => pacificToday(new Date(iso));

/** The leads touched today: their door is worked (D27). Follow-ups are not judged by this — a
 *  visit that sets a new action for this afternoon is still due, and D9 already says so. */
const touchedToday = (visits: readonly Visit[], today: IsoDate): Set<string> =>
  new Set(visits.filter((v) => pacificDay(v.visited_at) === today).map((v) => v.lead_id));

/** A lead the signal layer knows as a brand-new licence is still pre-open as far as the library's
 *  touch 3 is concerned — nothing else in the app records an opening date. */
const preOpenLeads = (signals: readonly Signal[]): Set<string> =>
  new Set(
    signals.flatMap((s) => (s.lead_id && s.event === 'new_business' ? [s.lead_id] : [])),
  );

export const buildBrief = (input: BriefInput): Brief => {
  const { today, leads, visits, signals, route, playbook } = input;
  const library = indexPlaybook(playbook);
  const touches = visitsByLead(visits);
  const preOpen = preOpenLeads(signals);
  const worked = touchedToday(visits, today);
  // a lead he will stand in front of today belongs to the route, not to two lists: the stop carries
  // the follow-up's own words as its reason, so nothing is lost by not repeating it
  const stopLeadIds = new Set((route?.stops ?? []).flatMap((s) => (s.lead_id ? [s.lead_id] : [])));
  const due = followUpTargets(leads, visits, today);
  const dueByLead = new Map(due.map((t) => [t.lead.id, t]));

  const followUps: BriefItem[] = due
    .filter(({ lead }) => !stopLeadIds.has(lead.id))
    .map(({ lead, note, daysOverdue }) => {
    const persona = personaForBusinessType(lead.business_type);
    const variants = followUpVariants(touches.get(lead.id) ?? 1, preOpen.has(lead.id));
    return {
      kind: 'follow_up' as const,
      daysOverdue,
      key: `follow_up:${lead.id}`,
      lead_id: lead.id,
      signal_id: null,
      name: lead.business_name,
      where: lead.address ?? lead.neighborhood,
      reason: note,
      phone: lead.phone,
      persona,
      done: false,
      ...draftsFor(library, persona, variants, leadFields(lead)),
    };
  });

  const stopSignals = new Set((route?.stops ?? []).flatMap((s) => (s.signal_id ? [s.signal_id] : [])));
  const stopLeads = new Set((route?.stops ?? []).flatMap((s) => (s.lead_id ? [s.lead_id] : [])));
  const leadById = new Map(leads.map((l) => [l.id, l]));

  const calls: BriefItem[] = signals
    .filter(
      (s) =>
        s.bucket === 'top' &&
        s.status === 'new' &&
        !!s.contact_phone &&
        !stopSignals.has(s.id) &&
        !(s.lead_id && (stopLeads.has(s.lead_id) || dueByLead.has(s.lead_id))),
    )
    .sort((a, b) => b.score - a.score || a.business_name.localeCompare(b.business_name))
    .slice(0, CALL_LIMIT)
    .map((signal) => {
      const persona = personaForSignal(signal.kind, signal.business_name);
      const why = whyText(signal);
      const filed = filedAgo(signal.filed_on, today).text;
      return {
        kind: 'call' as const,
        key: `call:${signal.id}`,
        lead_id: signal.lead_id,
        signal_id: signal.id,
        name: signal.business_name,
        where: [signal.address, signal.city].filter(Boolean).join(', ') || null,
        reason: [filed, why?.replace(/^Why: /, '')].filter(Boolean).join(' · '),
        phone: signal.contact_phone,
        persona,
        done: !!signal.lead_id && worked.has(signal.lead_id),
        ...draftsFor(library, persona, callVariants(persona, today), signalFields(signal)),
      };
    });

  const stops: BriefItem[] = (route?.stops ?? []).map((stop, i) => {
    const lead = stop.lead_id ? leadById.get(stop.lead_id) : undefined;
    const target = stop.lead_id ? dueByLead.get(stop.lead_id) : undefined;
    const persona = lead
      ? personaForBusinessType(lead.business_type)
      : personaForPlaceTypes(stop.types ?? []);
    const fields = lead
      ? leadFields(lead)
      : {
          business_name: stop.name,
          street_address: stop.address ?? undefined,
          street_name: streetNameOf(stop.address) ?? undefined,
        };
    return {
      kind: 'stop' as const,
      daysOverdue: target?.daysOverdue,
      key: `stop:${i}:${stop.place_id}`,
      lead_id: stop.lead_id ?? null,
      signal_id: stop.signal_id ?? null,
      name: stop.name,
      where: stop.address,
      reason:
        [
          stop.arrive ? `Arrive ${stop.arrive}` : null,
          // a route built by hand carries no note: the follow-up's own words stand in, so a door
          // taken off the follow-up list above still says why he is standing at it
          target?.note ?? stop.note,
        ]
          .filter(Boolean)
          .join(' · ') || 'On today’s route',
      phone: stop.phone ?? lead?.phone ?? null,
      persona,
      done: !target && !!stop.lead_id && worked.has(stop.lead_id),
      ...draftsFor(library, persona, target
        ? followUpVariants(touches.get(target.lead.id) ?? 1, preOpen.has(target.lead.id))
        : stopVariants(persona, today), fields),
    };
  });

  const gaps: string[] = [];
  if (playbook.length === 0)
    gaps.push('The message library has not reached this device yet — sync once online and the drafts appear.');
  if (!route || route.stops.length === 0)
    gaps.push('No route on this device for today — plan one from Route, or walk the calls below.');
  if (signals.length === 0)
    gaps.push('New businesses have not been stored on this device yet — open New businesses once online.');

  return {
    day: today,
    followUps,
    calls,
    stops,
    route: route
      ? { area: route.area ?? null, planned: route.planned === true, stops: route.stops.length }
      : null,
    gaps,
  };
};

/** How many things the day still asks of him: what is already worked does not count. */
export const briefCount = (brief: Brief): number =>
  [...brief.followUps, ...brief.calls, ...brief.stops].filter((i) => !i.done).length;
